import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { BehaviorSubject, catchError, firstValueFrom, map, of, tap, throwError } from "rxjs";
import { Client } from "../dashboard/interfaces/client.interface";
import { LockingPlan } from "../dashboard/interfaces/locking-plan.interface";
import { User } from "../dashboard/interfaces/user.interface";
import { ApiService } from "./api.service";

import { MatSnackBar } from "@angular/material/snack-bar";
import { UserRole } from "../dashboard/enums/role.enum";
import { ClientPriceGroup } from "../dashboard/interfaces/client-price-group.interface";
import { Customer } from "../dashboard/interfaces/customer.interface";
import {
    CylinderMaterial,
    CylinderOption,
    CylinderSize,
    CylinderSystem,
    CylinderType
} from "../dashboard/interfaces/cylinder-system.interface";
import { KeyBackOrder } from "../dashboard/interfaces/key-back-order.interface";
import { KeyedAlikeLockingPlan } from "../dashboard/interfaces/keyed-alike-locking-plan.interface";
import { LockingPlanBackOrder } from "../dashboard/interfaces/locking-plan-back-order.interface";

@Injectable({
  providedIn: 'root'
})
export class StoreService {
  private jwtHelper: JwtHelperService = new JwtHelperService();
  private currentUserSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  private clientsSubject: BehaviorSubject<Client[]> = new BehaviorSubject<Client[]>([]);
  private clientLockingPlanCylinderSystemsSubject: BehaviorSubject<CylinderSystem[]> = new BehaviorSubject<CylinderSystem[]>([]);
  private clientKeyedAlikeLockingPlanCylinderSystemsSubject: BehaviorSubject<CylinderSystem[]> = new BehaviorSubject<CylinderSystem[]>([]);
  private clientPriceGroupsSubject: BehaviorSubject<ClientPriceGroup[]> = new BehaviorSubject<ClientPriceGroup[]>([]);
  private pricesSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});
  private customersSubject: BehaviorSubject<Customer[]> = new BehaviorSubject<Customer[]>([]);
  private usersSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  private lockingPlansSubject: BehaviorSubject<LockingPlan[]> = new BehaviorSubject<LockingPlan[]>([]);
  private salesLockingPlansSubject: BehaviorSubject<LockingPlan[]> = new BehaviorSubject<LockingPlan[]>([]);
  private backOrderLockingPlansSubject: BehaviorSubject<LockingPlan[]> = new BehaviorSubject<LockingPlan[]>([]);
  private salesBackOrderLockingPlansSubject: BehaviorSubject<LockingPlan[]> = new BehaviorSubject<LockingPlan[]>([]);
  private lockingPlanBackOrdersSubject: BehaviorSubject<LockingPlanBackOrder[]> = new BehaviorSubject<LockingPlanBackOrder[]>([]);
  private salesLockingPlanBackOrdersSubject: BehaviorSubject<LockingPlanBackOrder[]> = new BehaviorSubject<LockingPlanBackOrder[]>([]);
  private keyBackOrdersPlansSubject: BehaviorSubject<KeyBackOrder[]> = new BehaviorSubject<KeyBackOrder[]>([]);
  private keyedAlikeLockingPlansSubject: BehaviorSubject<KeyedAlikeLockingPlan[]> = new BehaviorSubject<KeyedAlikeLockingPlan[]>([]);

  private cylinderSystemsSubject: BehaviorSubject<CylinderSystem[]> = new BehaviorSubject<CylinderSystem[]>([]);
  private cylinderTypesSubject: BehaviorSubject<CylinderType[]> = new BehaviorSubject<CylinderType[]>([]);
  private cylinderSizesSubject: BehaviorSubject<CylinderSize[]> = new BehaviorSubject<CylinderSize[]>([]);
  private cylinderMaterialsSubject: BehaviorSubject<CylinderMaterial[]> = new BehaviorSubject<CylinderMaterial[]>([]);
  private cylinderOptionsSubject: BehaviorSubject<CylinderOption[]> = new BehaviorSubject<CylinderOption[]>([]);
  private isLoggingOut = false;
  private hasShownSessionExpired = false;

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    this.currentUserSubject = new BehaviorSubject<User | null>(JSON.parse(localStorage.getItem('currentUser')!));
  }

  get user$() {
    return this.currentUserSubject;
  }

  get user(): User | null {
    return this.currentUserSubject.value;
  }

  get client(): Client | undefined {
    return this.currentUserSubject.value?.client;
  }

  get clientLockingPlanCylinderSystems(): CylinderSystem[] | undefined {
    return this.clientLockingPlanCylinderSystemsSubject.value;
  }

  get clientKeyedAlikeLockingPlanCylinderSystems(): CylinderSystem[] | undefined {
    return this.clientKeyedAlikeLockingPlanCylinderSystemsSubject.value;
  }

  get isAdmin(): boolean | null {
    return this.user!.role == UserRole.admin;
  }

  get isAnkerslotSales(): boolean | null {
    return this.user!.role == UserRole.admin || this.user!.role == UserRole.ankerslotSales
  }

  get isClientAdmin(): boolean | null {
    return this.user!.role == UserRole.admin || this.user!.role == UserRole.ankerslotSales || this.user!.role == UserRole.clientAdmin;
  }

  get isClientUser(): boolean | null {
    return this.user!.role == UserRole.clientAdmin || this.user!.role == UserRole.clientUser;
  }

  get isCustomerUser(): boolean | null {
    return this.user!.role == UserRole.customerUser;
  }


  get users$() {
    return this.usersSubject.asObservable();
  }
  get users() {
    return this.usersSubject.value;
  }

  loginUser(email: string, password: string ) {
    return this.apiService.login(email, password)
      .pipe(map((user: User) => {
        this.storeTokens(user);
        this.currentUserSubject.next(user);
        return user;
      }));
  }

  logoutUser(revokeSession: boolean = true) {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    const refreshToken = this.getRefreshToken();

    if (revokeSession && refreshToken) {
      this.apiService.logout(refreshToken).subscribe({
        error: () => undefined
      });
    }

    this.clearTokens();
    this.currentUserSubject.next(null);
    this.clearData();
    this.router.navigate(['/login']).catch();
  }

  storeTokens(user: User) {
    this.isLoggingOut = false;
    this.hasShownSessionExpired = false;
    localStorage.setItem('currentUser', JSON.stringify(user));
    localStorage.setItem('accessToken', user.accessToken);
    localStorage.setItem('refreshToken', user.refreshToken);
  };

  clearTokens() {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  };

  async ensureValidSession(): Promise<boolean> {
    if (!this.user) {
      return false;
    }

    const accessToken = localStorage.getItem('accessToken') ?? '';
    const refreshToken = this.getRefreshToken();

    if (accessToken && !this.jwtHelper.isTokenExpired(accessToken)) {
      return true;
    }

    if (!refreshToken) {
      this.resetSessionState();
      return false;
    }

    if (this.jwtHelper.isTokenExpired(refreshToken)) {
      this.handleSessionExpired(false);
      return false;
    }

    try {
      await firstValueFrom(this.getAccessToken());
      return true;
    } catch {
      return false;
    }
  }

  getAccessToken() {
    const refreshToken = this.getRefreshToken();
    return this.apiService.getAccessToken(refreshToken!).pipe(
      map((newAccessToken: string) => {
        localStorage.setItem('accessToken', newAccessToken);
        return newAccessToken;
      }),
      catchError((error) => {
        // If refresh token is expired (401), logout the user and show snackbar
        if (error.status === 401) {
          this.handleSessionExpired();
        }
        return throwError(() => error);
      })
    );
  }

  getRefreshToken(): string {
    return localStorage.getItem('refreshToken') ?? '';
  }

  getClients(refresh: boolean) {
    if (this.isAnkerslotSales) {
      if (this.clientsSubject.value.length == 0 || refresh) {
        return this.apiService.getClients().pipe(tap((data) => {
          this.clientsSubject.next(data);
        }));
      } else {
        return this.clientsSubject.asObservable();
      }
    } else {
      return of([]);
    }
  }

  getClientPriceGroups(refresh: boolean) {
    if (this.isAnkerslotSales) {
      if (this.clientPriceGroupsSubject.value.length == 0 || refresh) {
        return this.apiService.getClientPriceGroups().pipe(tap((data) => {
          this.clientPriceGroupsSubject.next(data);
        }));
      } else {
        return this.clientPriceGroupsSubject.asObservable();
      }
    } else {
      return of([]);
    }
  }


  getPrices(refresh: boolean) {
    if (this.isAnkerslotSales) {
      if (!this.pricesSubject.value.clientPriceGroups || refresh) {
        return this.apiService.getPrices().pipe(tap((data) => {
          this.pricesSubject.next(data);
        }));
      } else {
        return this.pricesSubject.asObservable();
      }
    } else {
      return of([]);
    }
  }


  getClientLockingPlanCylinderSystems(refresh: boolean) {
    if (this.clientLockingPlanCylinderSystemsSubject.value && this.clientLockingPlanCylinderSystemsSubject.value.length == 0 || refresh) {
      return this.apiService.getClientLockingPlanCylinderSystems(this.user?.client?.id!).pipe(tap((data) => {
        this.clientLockingPlanCylinderSystemsSubject.next(data);
      }));
    } else {
      return this.clientLockingPlanCylinderSystemsSubject.asObservable();
    }
  }

  getClientKeyedAlikeLockingPlanCylinderSystems(refresh: boolean) {
    if (this.clientKeyedAlikeLockingPlanCylinderSystemsSubject.value.length == 0 || refresh) {
      return this.apiService.getClientKeyedAlikeLockingPlanCylinderSystems(this.user?.client?.id!).pipe(tap((data) => {
        this.clientKeyedAlikeLockingPlanCylinderSystemsSubject.next(data);
      }));
    } else {
      return this.clientKeyedAlikeLockingPlanCylinderSystemsSubject.asObservable();
    }
  }

  // getClientProfilings(refresh: boolean) {
  //   if (this.clientProfilingsSubject.value.length == 0 || refresh) {
  //     return this.apiService.getClientProfilings().pipe(tap((data) => {
  //       this.clientProfilingsSubject.next(data);
  //     }));
  //   } else {
  //     return this.clientProfilingsSubject.asObservable();
  //   }
  // }

  // getClientGroups(refresh: boolean) {
  //   if (this.clientPriceGroupsSubject.value.length == 0 || refresh) {
  //     return this.apiService.getClientPriceGroups().pipe(tap((data) => {
  //       this.clientPriceGroupsSubject.next(data);
  //     }));
  //   } else {
  //     return this.clientPriceGroupsSubject.asObservable();
  //   }
  // }

  getCustomers(refresh: boolean) {
    if (this.customersSubject.value.length == 0 || refresh) {
      return this.apiService.getCustomers().pipe(tap((data) => {
        this.customersSubject.next(data);
      }));
    } else {
      return this.customersSubject.asObservable();
    }
  }

  getUsers(refresh: boolean) {
    if (this.usersSubject.value.length == 0 || refresh) {
      return this.apiService.getUsers().pipe(tap((data) => {
        this.usersSubject.next(data);
      }));
    } else {
      return this.usersSubject.asObservable();
    }
  }

  getLockingPlans(refresh: boolean) {
    if (this.lockingPlansSubject.value.length == 0 || refresh) {
      return this.apiService.getLockingPlans().pipe(tap((data) => {
        this.lockingPlansSubject.next(data);
      }));
    } else {
      return this.lockingPlansSubject.asObservable();
    }
  }

  getSalesLockingPlans(refresh: boolean) {
    if (this.salesLockingPlansSubject.value.length == 0 || refresh) {
      return this.apiService.getSalesLockingPlans().pipe(tap((data) => {
        this.salesLockingPlansSubject.next(data);
      }));
    } else {
      return this.salesLockingPlansSubject.asObservable();
    }
  }

  getBackorderLockingPlans(refresh: boolean) {
    if (this.lockingPlansSubject.value.length == 0 || refresh) {
      return this.apiService.getBackOrderLockingPlans().pipe(tap((data) => {
        this.backOrderLockingPlansSubject.next(data);
      }));
    } else {
      return this.backOrderLockingPlansSubject.asObservable();
    }
  }

  getSalesBackorderLockingPlans(refresh: boolean) {
    if (this.salesBackOrderLockingPlansSubject.value.length == 0 || refresh) {
      return this.apiService.getSalesBackOrderLockingPlans().pipe(tap((data) => {
        this.salesBackOrderLockingPlansSubject.next(data);
      }));
    } else {
      return this.salesBackOrderLockingPlansSubject.asObservable();
    }
  }

  getLockingPlanBackOrders(refresh: boolean) {
    if (this.lockingPlanBackOrdersSubject.value.length == 0 || refresh) {
      return this.apiService.getLockingPlanBackOrders().pipe(tap((data) => {
        this.lockingPlanBackOrdersSubject.next(data);
      }));
    } else {
      return this.lockingPlanBackOrdersSubject.asObservable();
    }
  }

  getSalesLockingPlanBackOrders(refresh: boolean) {
    if (this.salesLockingPlanBackOrdersSubject.value.length == 0 || refresh) {
      return this.apiService.getSalesLockingPlanBackOrders().pipe(tap((data) => {
        this.salesLockingPlanBackOrdersSubject.next(data);
      }));
    } else {
      return this.salesLockingPlanBackOrdersSubject.asObservable();
    }
  }

  getKeyBackOrders(refresh: boolean) {
    if (this.keyBackOrdersPlansSubject.value.length == 0 || refresh) {
      return this.apiService.getKeyBackOrders().pipe(tap((data) => {
        this.keyBackOrdersPlansSubject.next(data);
      }));
    } else {
      return this.keyBackOrdersPlansSubject.asObservable();
    }
  }

  getKeyedAlikeLockingPlans(refresh: boolean) {
    if (this.keyedAlikeLockingPlansSubject.value.length == 0 || refresh) {
      return this.apiService.getKeyedAlikeLockingPlans().pipe(tap((data) => {
        this.keyedAlikeLockingPlansSubject.next(data);
      }));
    } else {
      return this.keyedAlikeLockingPlansSubject.asObservable();
    }
  }

  getCylinderSystems(refresh: boolean) {
    if (this.cylinderSystemsSubject.value.length == 0 || refresh) {
      return this.apiService.getCylinderSystems().pipe(tap((data) => {
        this.cylinderSystemsSubject.next(data);
      }));
    } else {
      return this.cylinderSystemsSubject.asObservable();
    }
  }

  getCylinderTypes(refresh: boolean) {
    if (this.cylinderTypesSubject.value.length == 0 || refresh) {
      return this.apiService.getCylinderTypes().pipe(tap((data) => {
        this.cylinderTypesSubject.next(data);
      }));
    } else {
      return this.cylinderTypesSubject.asObservable();
    }
  }

  getCylinderSizes(refresh: boolean) {
    if (this.cylinderSizesSubject.value.length == 0 || refresh) {
      return this.apiService.getCylinderSizes().pipe(tap((data) => {
        this.cylinderSizesSubject.next(data);
      }));
    } else {
      return this.cylinderSizesSubject.asObservable();
    }
  }

  getCylinderMaterials(refresh: boolean) {
    if (this.cylinderMaterialsSubject.value.length == 0 || refresh) {
      return this.apiService.getCylinderMaterials().pipe(tap((data) => {
        this.cylinderMaterialsSubject.next(data);
      }));
    } else {
      return this.cylinderMaterialsSubject.asObservable();
    }
  }

  getCylinderOptions(refresh: boolean) {
    if (this.cylinderOptionsSubject.value.length == 0 || refresh) {
      return this.apiService.getCylinderOptions().pipe(tap((data) => {
        this.cylinderOptionsSubject.next(data);
      }));
    } else {
      return this.cylinderOptionsSubject.asObservable();
    }
  }

  private clearData() {
    this.currentUserSubject.next(null);
    this.clientsSubject.next([]);
    this.clientLockingPlanCylinderSystemsSubject.next([]);
    this.clientKeyedAlikeLockingPlanCylinderSystemsSubject.next([]);
    this.clientPriceGroupsSubject.next([]);
    this.customersSubject.next([]);
    this.usersSubject.next([]);
    this.lockingPlansSubject.next([]);
    this.backOrderLockingPlansSubject.next([]);
    this.salesBackOrderLockingPlansSubject.next([]);
    this.lockingPlanBackOrdersSubject.next([]);
    this.salesLockingPlanBackOrdersSubject.next([]);
    this.keyBackOrdersPlansSubject.next([]);
    this.keyedAlikeLockingPlansSubject.next([]);
    this.cylinderSystemsSubject.next([]);
    this.cylinderTypesSubject.next([]);
    this.cylinderSizesSubject.next([]);
    this.cylinderMaterialsSubject.next([]);
    this.cylinderOptionsSubject.next([]);
  }

  private resetSessionState() {
    this.clearTokens();
    this.clearData();
  }

  private handleSessionExpired(navigateToLogin: boolean = true) {
    if (!this.hasShownSessionExpired) {
      this.hasShownSessionExpired = true;
      this.snackBar.open('Session expired', '', {
        duration: 4000,
        panelClass: 'snackbar-error'
      });
    }

    this.resetSessionState();

    if (navigateToLogin) {
      this.router.navigate(['/login']).catch();
    }
  }
}

export const customersResolver: ResolveFn<Array<Customer>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getCustomers(false);
};

export const customerResolver: ResolveFn<Customer> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(ApiService).getCustomer(route.params['id']);
};


export const clientsResolver: ResolveFn<Array<Client>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getClients(false);
};


export const clientResolver: ResolveFn<Client> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(ApiService).getClient(route.params['id']);
};



export const clientPriceGroupsResolver: ResolveFn<Array<ClientPriceGroup>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getClientPriceGroups(false);
};

export const clientPriceGroupResolver: ResolveFn<ClientPriceGroup> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(ApiService).getClientPriceGroup(route.params['id']);
};



export const pricesResolver: ResolveFn<Array<any>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getPrices(true);
};


export const usersResolver: ResolveFn<Array<User>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getUsers(false);
};

export const userResolver: ResolveFn<Client> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(ApiService).getUser(route.params['id']);
};


export const lockingPlansResolver: ResolveFn<Array<LockingPlan>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getLockingPlans(false);
};

export const lockingPlanResolver: ResolveFn<LockingPlan> = (
  route: ActivatedRouteSnapshot,
) => {
  return inject(ApiService).getLockingPlan(route.params['id']);
};


export const salesLockingPlansResolver: ResolveFn<Array<LockingPlan>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getSalesLockingPlans(false);
};


export const salesLockingPlanResolver: ResolveFn<LockingPlan> = (
  route: ActivatedRouteSnapshot,
) => {
  return inject(ApiService).getSalesLockingPlan(route.params['id']);
};

export const backOrderLockingPlansResolver: ResolveFn<Array<LockingPlan>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getBackorderLockingPlans(true);
};


export const lockingPlanBackOrdersResolver: ResolveFn<Array<LockingPlan>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getLockingPlanBackOrders(false);
};

export const salesLockingPlanBackOrdersResolver: ResolveFn<Array<LockingPlanBackOrder>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getSalesLockingPlanBackOrders(false);
};


export const lockingPlanBackOrderResolver: ResolveFn<LockingPlan> = (
  route: ActivatedRouteSnapshot,
) => {
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);

  return inject(ApiService).getLockingPlanBackOrder(route.params['id']).pipe(catchError(err => {
    snackBar.open(err.error.message, '', {duration: 3000});
    router.navigate(['../'], {}).catch();
    throw err;
  }));
};

export const salesLockingPlanBackOrderResolver: ResolveFn<LockingPlanBackOrder> = (
  route: ActivatedRouteSnapshot,
) => {
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);

  return inject(ApiService).getSalesLockingPlanBackOrder(route.params['id']).pipe(catchError(err => {
    snackBar.open(err.error.message, '', {duration: 3000});
    router.navigate(['../'], {}).catch();
    throw err;
  }));
};

export const keyBackOrdersResolver: ResolveFn<Array<any>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getKeyBackOrders(false);
};

export const keyBackOrderResolver: ResolveFn<any> = (
  route: ActivatedRouteSnapshot,
) => {
  return inject(ApiService).getKeyBackOrder(route.params['id']);
};


export const keyedAlikeLockingPlansResolver: ResolveFn<Array<LockingPlan>> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(StoreService).getKeyedAlikeLockingPlans(false);
};


export const keyedAlikeLockingPlanResolver: ResolveFn<LockingPlan> = (
  route: ActivatedRouteSnapshot,
) => {
  return inject(ApiService).getKeyedAlikeLockingPlan(route.params['id']);
};



export const cylinderSystemsResolver: ResolveFn<Array<CylinderSystem>> = () => {
  return inject(StoreService).getCylinderSystems(false);
};

export const cylinderSystemResolver: ResolveFn<CylinderSystem> = (
  route: ActivatedRouteSnapshot,
) => {
  return inject(ApiService).getCylinderSystem(route.params['id']);
};

export const cylinderTypesResolver: ResolveFn<Array<CylinderType>> = () => {
  return inject(StoreService).getCylinderTypes(false);
};

export const cylinderTypeResolver: ResolveFn<CylinderType> = (
  route: ActivatedRouteSnapshot,
) => {
  return inject(ApiService).getCylinderType(route.params['id']);
};

export const cylinderSizesResolver: ResolveFn<Array<CylinderSize>> = () => {
  return inject(StoreService).getCylinderSizes(false);
};

export const cylinderMaterialsResolver: ResolveFn<Array<CylinderMaterial>> = () => {
  return inject(StoreService).getCylinderMaterials(false);
};


export const cylinderMaterialResolver: ResolveFn<CylinderMaterial> = (
  route: ActivatedRouteSnapshot,
) => {
  return inject(ApiService).getCylinderMaterial(route.params['id']);
};

export const cylinderOptionsResolver: ResolveFn<Array<CylinderOption>> = () => {
  return inject(StoreService).getCylinderOptions(false);
};
